import { useContext, useEffect, useState } from "react"

import "./SharedSpace.scss"
import { Navigate } from "react-router-dom"

import { SharedSpaceContext } from "~/providers/SharedSpaceProvider"
import UserGroup from "~/components/UserGroup/UserGroup"
import Timer from "~/components/Timer/Timer"
import Button from "~/components/Button/Button"
import TimerPicker from "~/components/TimerPicker/TimerPicker"
import PageContent from "~/components/PageContent/PageContent"
import PageWithSidebar from "~/components/PageWithSidebar/PageWithSidebar"
import NotificationModal from "~/components/NotificationModal/NotificationModal"
import SpaceActions from "../../components/SpaceActions/SpaceActions"

import axios from "axios"
import { postEvent } from "~/utils/EventManager"
import { setCycleFavicon } from "../../utils/Favicon"
import { showTimerEndedNotification } from "~/utils/NotificationUtils"
import { useParticipantNotifications } from "~/hooks/useParticipantNotifications"
import { useDeclareActiveSpacePage } from "~/providers/ActiveSpacePageProvider"

export default function SharedSpace() {
  const { space, cycle, switchCycle, shareToken } =
    useContext(SharedSpaceContext) ?? {}
  const [aTimerHasEnded, setATimerHasEnded] = useState(false)
  const timerIsRunning = Boolean(space?.current_timer)
  const timer = space?.current_timer
  useParticipantNotifications({ space })
  // Shared spaces run their own channel, so SpaceChannel keeps whatever
  // passive subscription it already had.
  useDeclareActiveSpacePage({ channelSpaceName: null })

  useEffect(() => {
    let timerTitleReminderOn = false
    const interval = setInterval(() => {
      if (space && aTimerHasEnded) {
        if (!space.current_timer) {
          document.title = timerTitleReminderOn
            ? "Pomodoro timer ended"
            : "Pomodoro"
          timerTitleReminderOn = !timerTitleReminderOn
        }
      }
    }, 3000)
    return () => {
      document.title = "Pomodoro"
      clearInterval(interval)
    }
  }, [space, aTimerHasEnded])

  useEffect(() => {
    if (timerIsRunning || !cycle) return

    setCycleFavicon(cycle)

    return () => setCycleFavicon("work")
  }, [cycle, timerIsRunning])

  if (!space) return <Navigate to="/" />

  const handleCreate = (minutes: string) => {
    const params = {
      timer: {
        duration: parseInt(minutes) * 60,
        break: cycle === "break",
      },
    }

    axios
      .post(`/api/shared_spaces/${shareToken}/timers`, params)
      .then(() => {
        postEvent({ shareToken }, "timerCreated")
      })
      .catch((error) => {
        console.error(error)
      })
  }

  const handleCancel = () => {
    axios.delete(`/api/shared_spaces/${shareToken}/timers/current`).then(() => {
      postEvent({ shareToken }, "timerCancelled")
    })
  }

  const handleEnded = () => {
    postEvent({ shareToken }, "timerEnded", { timer_id: timer?.id })
    setATimerHasEnded(true)
    showTimerEndedNotification(timer)
  }

  if (!cycle) return null

  return (
    <PageWithSidebar>
      <PageContent
        className={`SharedSpace__page-content--${
          cycle === "break" ? "break" : "work"
        }`}
      >
        <div className="Space">
          {timerIsRunning && space.current_timer ? (
            <Timer
              timer={space.current_timer}
              onEnded={handleEnded}
              onCancel={handleCancel}
            />
          ) : (
            <div className="SharedSpace__mode">
              <TimerPicker cycle={cycle} onCreate={handleCreate} />
              <Button
                onClick={switchCycle}
                style={"outline"}
                className={`SharedSpace__cycle--${cycle}`}
                text={cycle == "work" ? "Take a break" : "Back to work"}
              />
            </div>
          )}
        </div>
        <div className="SharedSpace__actions">
          <UserGroup
            hideTitle={false}
            guests={space.guests}
            users={space.users}
          />
          <SpaceActions space={space} />
        </div>
      </PageContent>
      <NotificationModal cycle={cycle} />
    </PageWithSidebar>
  )
}
